import { showConfirm } from '../components/ConfirmDialog';
import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useProfileContext } from '../hooks/useProfileContext';
import { useConfig } from '../hooks/useConfig';
import {
  apiGetSetlists,
  apiGetSetlistsUpcomingShow,
  apiGetBand,
  apiCreateSetlist,
  apiUpdateSetlist,
  apiDeleteSetlist,
  apiDuplicateSetlist,
  apiGetSongs,
  type SongDTO,
  type SetlistDTO,
  type SetlistSongDTO,
  type SetlistPdfColumns,
  defaultSetlistPdfColumns,
  type SetlistPdfStyle,
  defaultSetlistPdfStyle,
} from '../api';

// pdfmake shared helper
import { generatePdfBlobViaServer } from '../lib/pdf';
import PdfPreview from '../components/PdfPreview';
import { reportSaveError, reportSaveOk } from '../lib/saveStatus';
import PageHeader from '../components/PageHeader';
import { useWriteGate } from '../lib/writeGate';
import { getOfflineState } from '../lib/offline';
import type { TDocumentDefinitions } from 'pdfmake/interfaces';
import { t as pdfT } from '../lib/pdfI18n';

function newSong(order: number): SetlistSongDTO {
  return { id: crypto.randomUUID(), title: '', order };
}

function newComment(order: number): SetlistSongDTO {
  return { id: crypto.randomUUID(), title: '', order, is_separator: true };
}

type UpcomingShow = {
  id: number;
  name: string;
  date: string;
  venue_name?: string;
  venue_city?: string;
  tour_name?: string;
} | null;

function formatMmSs(totalSeconds?: number) {
  if (!totalSeconds || totalSeconds <= 0) return '';
  const m = Math.floor(totalSeconds / 60);
  const s = Math.floor(totalSeconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseDurationToSeconds(d?: string) {
  if (!d) return 0;
  const [m, s] = d.split(':').map(Number);
  if (Number.isNaN(m)) return 0;
  return m * 60 + (Number.isNaN(s) ? 0 : s);
}

export default function SetlistsPage() {
  const { t } = useTranslation();
  const { activeProfileId } = useProfileContext();
  const { config, updateSetlistPdf } = useConfig();
  // Capability-aware write gate (lot A2, flipped to the EXACT capabilities in
  // the A2-CAP pass, 2026-09-25). This page writes into TWO server families and
  // each one has its OWN capability — using one for the other would recreate
  // the very bug the gate removes (a 403 + red banner, or a silent no-save):
  //  · `content.setlists` → `/api/bands/:id/setlists` (CRUD). Mounted AFTER the
  //    server's global read-only guard (:497), exempted conditionally by the
  //    pre-guard mount at :447-452.
  //  · `content.rider`    → `PUT /api/bands/:id/rider/config`. The PDF
  //    columns/style autosave goes through `useConfig` (`updateSetlistPdf`), and
  //    the `setlistPdf` blob lives in `rider_config` → exempted by the pre-guard
  //    mount at :467-472, NOT by `content.setlists`.
  // Never a blanket `canWrite` here: it would freeze a read-only member the
  // capability was granted to.
  const writeGate = useWriteGate(activeProfileId);
  const canWriteSetlists = writeGate.canWriteCap('content.setlists');
  const canWriteRiderConfig = writeGate.canWriteCap('content.rider');
  // The note describes the page's REAL state: it must NOT tell a globally
  // read-only account that editing is disabled while the capability that
  // unlocks THIS page is held (the fields are open). Show it only when the
  // page's own write predicate is denied.
  const readOnlyNote = writeGate.readOnlyReason === 'global-read-only' && !canWriteSetlists;
  const [setlists, setSetlists] = useState<SetlistDTO[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState('');
  const [upcoming, setUpcoming] = useState<UpcomingShow>(null);
  const [pdfDoc, setPdfDoc] = useState<TDocumentDefinitions | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfColumns, setPdfColumns] = useState<SetlistPdfColumns>(defaultSetlistPdfColumns);
  const [pdfStyle, setPdfStyle] = useState<SetlistPdfStyle>(defaultSetlistPdfStyle);
  const [bandInfo, setBandInfo] = useState<{ id: string; name?: string; logo?: string; image_url?: string; image?: string } | null>(null);

  // ── "+ Add from songbook" picker ──────────────────────────────────────
  // Opens a modal listing the band's songbook; clicking a song inserts a
  // pre-filled setlist line (metadata copied + song_id trace). Lazy-loads the
  // songbook the first time the picker is opened, then reuses the cached list.
  const [pickerOpen, setPickerOpen] = useState(false);
  const [bookSongs, setBookSongs] = useState<SongDTO[] | null>(null);
  const [bookSearch, setBookSearch] = useState('');
  const [bookLoading, setBookLoading] = useState(false);

  // ── Persisted setlist-PDF settings (per band) ──────────────────────────
  // Hydrate from the band config ONLY when the saved value comes from the
  // server (i.e. differs from what we last wrote ourselves) — otherwise the
  // persist→config→hydrate loop never stops. `lastPersistedRef` holds the JSON
  // we wrote so we can tell our own writes apart from a fresh load.
  const lastPersistedRef = useRef<string>('');
  // Latest full setlist draft awaiting a debounced save, and its timer (see
  // updateSelected): serializes rapid edits so the PUT that finally fires
  // carries the freshest state — never a stale snapshot that could overwrite
  // newer edits (offline replay included).
  const latestDraft = useRef<SetlistDTO | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const saved = config?.setlistPdf;
    if (!saved) return;
    const savedJson = JSON.stringify(saved);
    if (savedJson === lastPersistedRef.current) return; // our own write — ignore
    setPdfColumns(() => ({
      ...defaultSetlistPdfColumns(),
      ...(saved.columns || {}),
    }));
    setPdfStyle(() => ({
      ...defaultSetlistPdfStyle(),
      ...(saved.style || {}),
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  // Persist the PDF columns/style to the band config (debounced) so the next
  // visit keeps the same choices. Record what we wrote so hydration ignores it.
  useEffect(() => {
    // A2-CAP: content.rider
    if (!canWriteRiderConfig) return; // read-only: never fire the config write
    const payload = {
      columns: pdfColumns as unknown as Record<string, boolean>,
      style: pdfStyle as unknown as Record<string, unknown>,
    };
    lastPersistedRef.current = JSON.stringify(payload);
    updateSetlistPdf(payload);
  }, [pdfColumns, pdfStyle, updateSetlistPdf, canWriteRiderConfig]);


  const load = useCallback(() => {
    if (!activeProfileId) return;
    setLoading(true);
    Promise.all([
      apiGetSetlists(activeProfileId),
      apiGetSetlistsUpcomingShow(activeProfileId),
      apiGetBand(activeProfileId),
    ])
      .then(([data, show, band]) => {
        // Ensure every setlist has a songs array; normalize the song order
        // (DB column is `position`, frontend DTO expects `order`).
        data.forEach((s) => {
          s.songs = (s.songs || []).map((sg) => ({ ...sg, order: sg.order ?? sg.position ?? 0 }));
        });
        setSetlists(data);
        setSelectedId((prev) => (prev && data.find((s) => s.id === prev)) ? prev : data[0]?.id ?? null);
        setUpcoming(show);
        setBandInfo(band);
      })
      .catch((e) => reportSaveError('setlist', e))
      .finally(() => setLoading(false));
  }, [activeProfileId]);

  useEffect(() => { load(); }, [load]);

  // Update live PDF preview whenever the selected setlist changes (debounced so a
  // burst of keystrokes in the editor does NOT fire one server rasterization
  // (/api/pdf/pages) per character). PdfPreview keeps the previous pages visible
  // during regeneration (stale-while-revalidate), so debouncing here does not
  // cause a jump — it only batches the rasterization work.
  useEffect(() => {
    const selected = setlists.find((s) => s.id === selectedId) || null;
    if (!selected || !activeProfileId || !bandInfo) {
      setPdfDoc(null);
      setPdfError(null);
      return;
    }
    setPdfError(null);
    const timer = window.setTimeout(() => {
      try {
        setPdfDoc(buildDocContent(bandInfo, selected, upcoming, pdfColumns, pdfStyle) as TDocumentDefinitions);
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        setPdfDoc(null);
        setPdfError(msg);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [selectedId, setlists, upcoming, activeProfileId, bandInfo, pdfColumns, pdfStyle]);

  if (!activeProfileId) {
    return <div className="page"><div className="loading">{t('setlists.selectBandFirst')}</div></div>;
  }

  const selected = setlists.find((s) => s.id === selectedId) || null;

  const createSetlist = async () => {
    // A2-CAP: content.setlists
    if (!canWriteSetlists) return;
    try {
      const s = await apiCreateSetlist(activeProfileId, {
        name: name.trim() || t('setlists.newSetlistName'),
        songs: [newSong(0)],
      });
      setName('');
      await load();
      setSelectedId(s.id);
      reportSaveOk('setlist');
    } catch (err) {
      reportSaveError('setlist', err);
    }
  };

  const updateSelected = async (patch: Partial<SetlistDTO>) => {
    // A2-CAP: content.setlists
    if (!canWriteSetlists) return;
    if (!selected) return;
    const updated = { ...selected, ...patch };
    setSetlists((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));

    /* Serialize + debounce saves. Each keystroke builds `updated` from the
       CURRENT React `selected`, so consecutive rapid PUTs can race and the
       last one flushed may carry a STALE snapshot that overwrites the newer
       state. We track the latest snapshot and only send a PUT once typing
       settles (400 ms) — and it always sends the FRESHEST full state, so the
       request that actually reaches the server (or the offline queue) holds
       every edit. This also batches the per-keystroke replay when offline. */
    latestDraft.current = updated;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const draft = latestDraft.current;
      latestDraft.current = null;
      if (!draft) return;
      try {
        await apiUpdateSetlist(activeProfileId, draft.id, draft);
        // Only refetch after a save that actually reached the network. When the
        // save was queued offline (optimistic reply, still offline), a load()
        // would re-read the STALE cached collection (a single-setlist PUT is not
        // mirrored into the /setlists list cache) and reset the editor to the
        // pre-edit state mid-typing. The next debounced PUT would then rebuild
        // from that stale base and, via per-URL coalescing, silently overwrite
        // the good queued edit in the offline queue → edits lost on sync.
        if (!getOfflineState().offline) {
          await load();
        }
        reportSaveOk('setlist');
      } catch (err) {
        reportSaveError('setlist', err);
      }
    }, 400);
  };

  const removeSetlist = async (id: string) => {
    // A2-CAP: content.setlists
    if (!canWriteSetlists) return;
    if (!(await showConfirm(t('setlists.deleteConfirm')))) return;
    try {
      await apiDeleteSetlist(activeProfileId, id);
      setSelectedId(null);
      load();
      reportSaveOk('setlist');
    } catch (err) {
      reportSaveError('setlist', err);
    }
  };

  const duplicateSetlist = async (id: string) => {
    // A2-CAP: content.setlists
    if (!canWriteSetlists) return;
    try {
      await apiDuplicateSetlist(activeProfileId, id);
      await load();
      const dup = (setlistsAfterLoad(id));
      if (dup) setSelectedId(dup.id);
      reportSaveOk('setlist');
    } catch (err) {
      reportSaveError('setlist', err);
    }
  };
  const setlistsAfterLoad = (id: string) => setlists.find((s) => s.id === id);

  const addSong = () => {
    if (!selected) return;
    updateSelected({ songs: [...(selected.songs || []), newSong((selected.songs || []).length)] });
  };

  const addComment = () => {
    if (!selected) return;
    updateSelected({ songs: [...(selected.songs || []), newComment((selected.songs || []).length)] });
  };

  /** Open the songbook picker. Lazy-loads the band's songbook the first time. */
  const openFromBook = async () => {
    if (!activeProfileId) return;
    setPickerOpen(true);
    setBookSearch('');
    if (bookSongs === null) {
      setBookLoading(true);
      try {
        setBookSongs(await apiGetSongs(activeProfileId));
      } catch (err) {
        reportSaveError('songbook', err);
        setBookSongs([]);
      } finally {
        setBookLoading(false);
      }
    }
  };

  /** Insert a songbook song as a new line in the selected setlist. Metadata is
   * copied at insert time (title/artist/duration/keys/tempo/notes + song_id) —
   * the copied line stays independently editable in the setlist. */
  const insertFromBook = (song: SongDTO) => {
    if (!selected) return;
    const order = (selected.songs || []).length;
    const line: SetlistSongDTO = {
      id: crypto.randomUUID(),
      title: song.title || '',
      artist: song.artist || '',
      duration: song.duration || '',
      keys: song.keys || '',
      tempo: song.tempo || '',
      notes: song.notes || '',
      order,
      song_id: song.id != null ? Number(song.id) : null,
    };
    updateSelected({ songs: [...(selected.songs || []), line] });
    setPickerOpen(false);
    reportSaveOk('setlist');
  };

  const updateSong = (id: string, patch: Partial<SetlistSongDTO>) => {
    if (!selected) return;
   updateSelected({ songs: (selected.songs || []).map((s) => (s.id === id ? { ...s, ...patch } : s)) });
  };

  const removeSong = (id: string) => {
    if (!selected) return;
    const reordered = (selected.songs || [])
      .filter((s) => s.id !== id)
      .sort((a, b) => a.order - b.order)
      .map((s, i) => ({ ...s, order: i }));
    updateSelected({ songs: reordered });
  };

  const moveSong = (id: string, dir: -1 | 1) => {
    if (!selected) return;
    const sorted = [...(selected.songs || [])].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((s) => s.id === id);
    const swap = idx + dir;
    if (swap < 0 || swap >= sorted.length) return;
    // Move the item physically in the array, then reindex every song's order
    // to its new position. This keeps the persisted order consistent with the
    // visual order (the backend writes position from the array index).
    const reordered = [...sorted];
    [reordered[idx], reordered[swap]] = [reordered[swap], reordered[idx]];
    const reindexed = reordered.map((s, i) => ({ ...s, order: i }));
    updateSelected({ songs: reindexed });
  };

  const exportJson = () => {
    if (!selected) return;
    const blob = new Blob([JSON.stringify(selected, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selected.name || 'setlist'}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const printPdf = () => {
    if (!selected || !activeProfileId || !bandInfo) return;
    const docDef = buildDocContent(bandInfo, selected, upcoming, pdfColumns, pdfStyle);
    generatePdfBlobViaServer(docDef)
      .then((blobUrl) => {
        const iframe = document.createElement('iframe');
        iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
        iframe.src = blobUrl;
        iframe.onload = () => {
          try { iframe.contentWindow?.focus(); iframe.contentWindow?.print(); } catch {}
          setTimeout(() => { iframe.remove(); URL.revokeObjectURL(blobUrl); }, 1000);
        };
        document.body.appendChild(iframe);
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setPdfError(msg);
      });
  };

  const downloadPdf = () => {
    if (!selected || !activeProfileId || !bandInfo) return;
    const docDef = buildDocContent(bandInfo, selected, upcoming, pdfColumns, pdfStyle);
    generatePdfBlobViaServer(docDef)
      .then((blobUrl) => {
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `${(selected.name || 'setlist').replace(/[^\\w\\-]+/g, '_')}.pdf`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
      })
      .catch((err) => {
        const msg = err instanceof Error ? err.message : String(err);
        setPdfError(msg);
      });
  };

  const sortedSongs = [...(selected?.songs || [])].sort((a, b) => a.order - b.order);
  const totalSeconds = sortedSongs.reduce((sum, s) => sum + parseDurationToSeconds(s.duration), 0);
  // Sequential song numbers (Comments / separators don't consume a number).
  const songNumber = new Map<string, number>();
  let songCounter = 0;
  for (const s of sortedSongs) {
    if (s.is_separator) continue;
    songCounter += 1;
    songNumber.set(s.id, songCounter);
  }

  return (
    <div className="page">
      {/* P3a — unified page header: mono title at the left, the create
          controls as the page actions at the right (same gate as before). */}
      <PageHeader
        title={t('setlists.title')}
        actions={
          <>
            <input
              className="form-input"
              placeholder={t('setlists.setlistName')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              style={{ width: 200, maxWidth: '100%' }}
              data-setlist-new-name
              disabled={
                // A2-CAP: content.setlists
                !canWriteSetlists
              }
            />
            <button className="btn btn-primary" onClick={createSetlist} disabled={
              // A2-CAP: content.setlists
              !canWriteSetlists
            }>
              {t('setlists.newSetlist')}
            </button>
          </>
        }
      />

      {readOnlyNote && (
        <p className="text-dim" style={{ color: 'var(--text-dim)', fontSize: 'var(--font-sm)', margin: '0 0 12px' }}>
          {t('common.readOnlyAccount')}
        </p>
      )}

      <div className="setlists-layout" style={{ gap: 20 }}>
        {/* Setlist list — P3a card stack (the selected row carries `.on`). */}
        <div>
          {loading ? <div className="loading">{t('setlists.loading')}</div> : setlists.length === 0 ? (
            <p style={{ color: 'var(--text-dim)' }}>{t('setlists.noSetlists')}</p>
          ) : (
            <div className="rc-list" data-setlists-list>
              {setlists.map((s) => (
                <div
                  key={s.id}
                  className={`rc-card clickable${s.id === selectedId ? ' on' : ''}`}
                  data-setlist-item={s.id}
                  onClick={() => setSelectedId(s.id)}
                >
                  <div className="rc-card-body">
                    <div className="rc-card-name">{s.name}</div>
                  </div>
                  <span className="rc-chip">{(s.songs || []).length}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Selected setlist editor + live PDF preview */}
        <div>
          {!selected ? (
            <div className="card" style={{ textAlign: 'center', padding: 48 }}>
              <p style={{ color: 'var(--text-dim)' }}>{t('setlists.selectOrCreate')}</p>
            </div>
          ) : (
            <div className="card">
              <div className="rc-header" style={{ marginBottom: 16 }}>
                <input className="form-input" style={{ fontSize: 'var(--font-xl)', fontWeight: 600 }}
                  data-setlist-name
                  value={selected.name}
                  onChange={(e) => updateSelected({ name: e.target.value })}
                  disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}
                  placeholder={t('setlists.setlistName')} />
                <div className="rc-header-actions">
                  <button className="btn btn-secondary" onClick={exportJson}>{t('setlists.export')}</button>
                  <button className="btn btn-secondary" onClick={downloadPdf}>{t('setlists.exportPdf')}</button>
                  <button className="btn btn-secondary" onClick={printPdf}>{t('setlists.print')}</button>
                  <button className="btn btn-secondary" onClick={() => duplicateSetlist(selected.id)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>{t('setlists.duplicate')}</button>
                  <button className="btn btn-danger" onClick={() => removeSetlist(selected.id)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>{t('setlists.delete')}</button>
                </div>
              </div>

              <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-dim)', display: 'flex', alignItems: 'center' }}>
                  {totalSeconds > 0 && t('setlists.totalDuration', { time: formatMmSs(totalSeconds) })}
                </span>
              </div>

              <ol style={{ listStyle: 'none', padding: 0, counterReset: 'song' }}>
                {sortedSongs.map((song) => song.is_separator ? (
                  <li key={song.id} className="rc-form-row" style={{
                    background: 'var(--surface-hover)',
                  }}>
                    <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {t('setlists.section')}
                    </span>
                    <input className="form-input" style={{ flex: 1, fontWeight: 600 }}
                      placeholder={t('setlists.commentPh')}
                      value={song.title}
                      onChange={(e) => updateSong(song.id, { title: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <button className="btn btn-small btn-secondary" onClick={() => moveSong(song.id, -1)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>↑</button>
                    <button className="btn btn-small btn-secondary" onClick={() => moveSong(song.id, 1)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>↓</button>
                    <button className="btn btn-small btn-danger" onClick={() => removeSong(song.id)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>✕</button>
                  </li>
                ) : (
                  <li key={song.id} className="rc-form-row">
                    <span style={{ width: 24, textAlign: 'center', color: 'var(--text-dim)' }}>
                      {songNumber.get(song.id) ?? ''}
                    </span>
                    <input className="form-input" style={{ flex: 2 }} placeholder={t('setlists.titlePh')}
                      value={song.title}
                      onChange={(e) => updateSong(song.id, { title: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <input className="form-input" style={{ flex: 1 }} placeholder={t('setlists.remarksPh')}
                      value={song.remarks || ''}
                      onChange={(e) => updateSong(song.id, { remarks: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <input className="form-input" style={{ flex: 1 }} placeholder={t('setlists.durationPh')}
                      value={song.duration || ''}
                      onChange={(e) => updateSong(song.id, { duration: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <input className="form-input" style={{ width: 90 }} placeholder={t('setlists.tempoPh')}
                      value={song.tempo || ''}
                      onChange={(e) => updateSong(song.id, { tempo: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <input className="form-input" style={{ width: 90 }} placeholder={t('setlists.keyPh')}
                      value={song.keys || ''}
                      onChange={(e) => updateSong(song.id, { keys: e.target.value })}
                      disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
} />
                    <button className="btn btn-small btn-secondary" onClick={() => moveSong(song.id, -1)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>↑</button>
                    <button className="btn btn-small btn-secondary" onClick={() => moveSong(song.id, 1)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>↓</button>
                    <button className="btn btn-small btn-danger" onClick={() => removeSong(song.id)} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>✕</button>
                  </li>
                ))}
              </ol>
              <button className="btn btn-primary" style={{ marginTop: 12, marginRight: 8 }} onClick={addSong} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>
                {t('setlists.addSong')}
              </button>
              <button className="btn btn-secondary" style={{ marginTop: 12, marginRight: 8 }} onClick={openFromBook} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>
                {t('setlists.addFromBook')}
              </button>
              <button className="btn btn-secondary" style={{ marginTop: 12 }} onClick={addComment} disabled={
  // A2-CAP: content.setlists
  !canWriteSetlists
}>
                {t('setlists.addComment')}
              </button>

              {/* PDF options: column picker + style mini-editor */}
              <div style={{ marginTop: 24, border: '1px solid var(--border)', borderRadius: 8, padding: 14 }}>
                <div style={{ display: 'flex', gap: 14, alignItems: 'center', marginBottom: 12, flexWrap: 'wrap' }}>
                  <span style={{ color: 'var(--text-dim)', fontSize: '0.85rem', fontWeight: 600 }}>{t('setlists.pdfColumns')}</span>
                  {([
                    ['showNumber', '#'],
                    ['title', t('setlists.colTitle')],
                    ['remarks', t('setlists.colRemarks')],
                    ['duration', t('setlists.colDuration')],
                    ['tempo', t('setlists.colTempo')],
                    ['key', t('setlists.colKey')],
                  ] as [keyof SetlistPdfColumns, string][]).map(([key, label]) => (
                    <label key={key} className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={pdfColumns[key]}
                        onChange={(e) => setPdfColumns((prev) => ({ ...prev, [key]: e.target.checked }))}
                        disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                      />
                      <span style={{ marginLeft: 5 }}>{label}</span>
                    </label>
                  ))}
                  <label className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer', marginLeft: 8 }}>
                    <input
                      type="checkbox"
                      checked={pdfColumns.showTableHeader}
                      onChange={(e) => setPdfColumns((prev) => ({ ...prev, showTableHeader: e.target.checked }))}
                      disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                    />
                    <span style={{ marginLeft: 5 }}>{t('setlists.tableHeader')}</span>
                  </label>
                </div>

                {/* Style mini-editor */}
                <div style={{ display: 'flex', gap: 18, alignItems: 'center', flexWrap: 'wrap', borderTop: '1px solid var(--border)', paddingTop: 12 }}>
                  <label className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pdfStyle.showGrid}
                      onChange={(e) => setPdfStyle((p) => ({ ...p, showGrid: e.target.checked }))} disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
} />
                    <span style={{ marginLeft: 5 }}>{t('setlists.showGrid')}</span>
                  </label>
                  <label className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                    <input type="checkbox" checked={pdfStyle.uppercase}
                      onChange={(e) => setPdfStyle((p) => ({ ...p, uppercase: e.target.checked }))} disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
} />
                    <span style={{ marginLeft: 5 }}>{t('setlists.uppercase')}</span>
                  </label>
                  <label className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={pdfStyle.multiPage}
                                        onChange={(e) => setPdfStyle((p) => ({ ...p, multiPage: e.target.checked }))} disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
} />
                                      <span style={{ marginLeft: 5 }}>{t('setlists.multiPage')}</span>
                                    </label>
                                    <label className="checkbox-label" style={{ fontSize: '0.85rem', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={pdfStyle.showLogo}
                                        onChange={(e) => setPdfStyle((p) => ({ ...p, showLogo: e.target.checked }))} disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
} />
                                      <span style={{ marginLeft: 5 }}>{t('setlists.showLogo')}</span>
                                    </label>
                                    <label style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                                      {t('setlists.logoSize')}
                                      <input type="number" min={40} max={300} value={pdfStyle.logoWidth}
                                        onChange={(e) => setPdfStyle((p) => ({ ...p, logoWidth: Number(e.target.value) || 257 }))}
                                        disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                                        style={{ width: 52, marginLeft: 6, padding: '2px 4px' }} />
                                    </label>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('setlists.fontSize')}
                    <input type="number" min={8} max={32} value={pdfStyle.bodyFontSize}
                      onChange={(e) => setPdfStyle((p) => ({ ...p, bodyFontSize: Number(e.target.value) || 11 }))}
                      disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                      style={{ width: 52, marginLeft: 6, padding: '2px 4px' }} />
                  </label>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('setlists.zebra')}
                    <input type="color" value={pdfStyle.zebraFill}
                      onChange={(e) => setPdfStyle((p) => ({ ...p, zebraFill: e.target.value }))}
                      disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                      style={{ marginLeft: 6, width: 36, height: 24, padding: 0 }} />
                  </label>
                  <label style={{ fontSize: '0.85rem', color: 'var(--text-dim)' }}>
                    {t('setlists.headerFill')}
                    <input type="color" value={pdfStyle.headerFill}
                      onChange={(e) => setPdfStyle((p) => ({ ...p, headerFill: e.target.value }))}
                      disabled={
  // A2-CAP: content.rider
  !canWriteRiderConfig
}
                      style={{ marginLeft: 6, width: 36, height: 24, padding: 0 }} />
                  </label>
                </div>
              </div>

              <div style={{ marginTop: 24 }}>
                <h3 style={{ marginBottom: 8 }}>{t('setlists.pdfPreview')}</h3>
                {pdfError ? (
                  <p style={{ color: 'var(--danger)' }}>{t('setlists.previewError', { msg: pdfError })}</p>
                ) : pdfDoc ? (
                  <PdfPreview docDef={pdfDoc} />
                ) : (
                  <p style={{ color: 'var(--text-dim)' }}>{t('setlists.loadingPreview')}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* "+ Add from songbook" picker modal */}
      {pickerOpen && (
        <div
          className="modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label={t('setlists.addFromBook')}
          onClick={(e) => { if (e.target === e.currentTarget) setPickerOpen(false); }}
        >
          <div className="modal" style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 className="modal-title">{t('setlists.addFromBook')}</h3>
              <button type="button" className="modal-close" aria-label={t('songbook.close')}
                onClick={() => setPickerOpen(false)}>✕</button>
            </div>
            <div className="modal-body">
              <input
                className="form-input"
                placeholder={t('songbook.searchPh')}
                value={bookSearch}
                onChange={(e) => setBookSearch(e.target.value)}
                aria-label={t('songbook.searchPh')}
                autoFocus
                style={{ marginBottom: 12 }}
              />
              {bookLoading ? (
                <div className="loading">{t('setlists.loading')}</div>
              ) : !bookSongs || bookSongs.length === 0 ? (
                <p style={{ color: 'var(--text-dim)' }}>{t('setlists.noBookSongs')}</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, maxHeight: 320, overflowY: 'auto' }}>
                  {bookSongs
                    .filter((s) => !bookSearch.trim() ||
                      (s.title || '').toLowerCase().includes(bookSearch.trim().toLowerCase()) ||
                      (s.artist || '').toLowerCase().includes(bookSearch.trim().toLowerCase()))
                    .map((song) => (
                      <li key={song.id} style={{ marginBottom: 6 }}>
                        <button
                          className="btn btn-secondary"
                          style={{ width: '100%', justifyContent: 'space-between', display: 'flex', textAlign: 'left' }}
                          onClick={() => insertFromBook(song)}
                        >
                          <span style={{ fontWeight: 600 }}>{song.title || '—'}</span>
                          <span style={{ fontSize: 'var(--font-xs)', color: 'var(--text-dim)', whiteSpace: 'nowrap' }}>
                            {[song.artist, song.duration].filter(Boolean).join(' · ')}
                          </span>
                        </button>
                      </li>
                    ))}
                </ul>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setPickerOpen(false)}>
                {t('songbook.cancel')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function isBase64ImageUrl(url?: string) {
  return typeof url === 'string' && /^data:image\/(png|jpeg|jpg|webp);base64,/.test(url);
}

export function buildDocContent(
  band: { id: string; name?: string; logo?: string; image_url?: string; image?: string },
  setlist: SetlistDTO,
  upcoming: UpcomingShow,
  columns?: SetlistPdfColumns,
  style?: SetlistPdfStyle,
): TDocumentDefinitions {
  const col = columns ?? defaultSetlistPdfColumns();
  const st = style ?? defaultSetlistPdfStyle();
  const songs = [...(setlist.songs || [])].sort((a, b) => a.order - b.order);
  const totalSeconds = songs.reduce((sum, s) => sum + parseDurationToSeconds(s.duration), 0);
  // A setlist is reusable across many shows, so it carries no single show date.
  // `upcoming` (optional) only supplies a show name/venue context when provided.
  // If the show belongs to a tour, show the TOUR name instead of the show name.
  const hasTour = !!upcoming?.tour_name;
  const showName = upcoming?.tour_name || upcoming?.name;
  // With a tour: show "venue, city · date". Without a tour: show "city · date"
  // (no venue name) — just the place + date.
  const venueParts = hasTour
    ? [upcoming?.venue_name, upcoming?.venue_city].filter(Boolean)
    : [upcoming?.venue_city].filter(Boolean);
  const venue = venueParts.join(', ');

  const imageSrc = isBase64ImageUrl(band.logo) ? band.logo
    : isBase64ImageUrl(band.image_url) ? band.image_url
    : isBase64ImageUrl(band.image) ? band.image
    : null;

  const headerStack: any[] = [];
  // Logo (size configurable, can be hidden). Centered.
  if (imageSrc && st.showLogo) {
    headerStack.push({ image: imageSrc, width: st.logoWidth, alignment: 'center', margin: [0, 0, 0, 10] });
  }
  // If no logo (or logo hidden), show the band name instead.
  if ((!imageSrc || !st.showLogo) && band.name) {
    headerStack.push({ text: band.name, style: 'band', alignment: 'center' });
  }
  // Show name (big), then venue + date (small), then setlist name (small).
  if (showName) {
    headerStack.push({ text: showName, style: 'title', alignment: 'center' });
  }
  const venueLine = [venue, upcoming?.date].filter(Boolean).join('  ·  ');
  if (venueLine) {
    headerStack.push({ text: venueLine, style: 'venue', alignment: 'center' });
  }
  headerStack.push({ text: setlist.name || pdfT('pdf.setlist.setlistLower'), style: 'subtitle', alignment: 'center' });
  headerStack.push({ text: ' ', margin: [0, 10, 0, 10] });

  const body: any[] = [headerStack];

  if (songs.length === 0) {
    body.push({ text: pdfT('pdf.setlist.noSongsYet'), italics: true, color: '#999', alignment: 'center' });
  } else {
    // Stretch row height so the table fills the page, staying on one page.
    // pdfmake ignores `minRowHeight` in this build, so we force height via the
    // cell vertical margin (`vpad`). We estimate the REAL number of wrapped
    // lines per title (based on the Title column width) instead of a flat
    // per-line penalty — this fills the page when titles are short while still
    // staying on one page when titles wrap.
    const PAGE_W = 595; // A4 width in pt
    const PAGE_H = 842;
    const MARGIN_TB = 80;
    const HEADER_EST = 300; // real header height incl. optional "Total" line (large logo + show/venue/setlist)
    const SECTION_EST = 19; // "Setlist" label
    const TOTAL_EST = 27; // "Total duration" line (when durations present)
    const SAFETY = 10; // small buffer for table borders / section margin
    const LINE_NATURAL = st.bodyFontSize + 2; // ~13pt natural line height at 11pt font
    // Title column width ≈ usable width minus the other columns
    const USABLE_W = PAGE_W - 80; // page width - left/right margins
    const OTHER_W = 16 + 38 + 30 + 24 + 70; // # , Duration, Tempo, Key, Remarks(auto)
    const TITLE_W = USABLE_W - OTHER_W;
    const CHARS_PER_LINE = Math.max(10, Math.floor(TITLE_W / (st.bodyFontSize * 0.56))); // ~6.2pt/char at 11pt
    let naturalLines = 1; // header row
    for (const s of songs) {
      if (s.is_separator) naturalLines += 1;
      else naturalLines += Math.max(1, Math.ceil((s.title || '—').length / CHARS_PER_LINE));
    }
    const available = PAGE_H - MARGIN_TB - HEADER_EST - SECTION_EST - TOTAL_EST - SAFETY;
    // In single-page mode we stretch rows (vpad) to fill one A4 page. In
    // multi-page mode we do NOT stretch — the table flows naturally and can
    // spill onto further pages (each page gets a "Page X / Y" header/footer).
    const vpad = st.multiPage ? 0 : Math.max(0, Math.min(150, (available - naturalLines * LINE_NATURAL) / (2 * naturalLines)));
    const cellPad: any = { margin: [4, vpad, 4, vpad], valign: 'middle' };

    // Build the column list from the enabled flags, in display order. A data
    // column that has NO value across all songs is dropped from the PDF even if
    // its checkbox is on (e.g. Tempo empty everywhere → no Tempo column).
    const songColValue = (key: Exclude<keyof SetlistPdfColumns, 'showTableHeader' | 'showNumber'>, s: SetlistSongDTO): string => {
      switch (key) {
        case 'title': return (s.title || '').trim();
        case 'remarks': return (s.remarks || '').trim();
        case 'duration': return (s.duration || '').trim();
        case 'tempo': return (s.tempo || '').trim();
        case 'key': return (s.keys || '').trim();
        default: return '';
      }
    };
    const colHasValue = (key: Exclude<keyof SetlistPdfColumns, 'showTableHeader' | 'showNumber'>) =>
      songs.some((s) => songColValue(key, s));

    const enabledCols: { key: keyof SetlistPdfColumns; label: string; width: string }[] = [];
    if (col.showNumber) enabledCols.push({ key: 'showNumber', label: '#', width: 'auto' });
    if (col.title && colHasValue('title')) enabledCols.push({ key: 'title', label: pdfT('pdf.setlist.title'), width: '*' });
    if (col.remarks && colHasValue('remarks')) enabledCols.push({ key: 'remarks', label: pdfT('pdf.setlist.remarks'), width: 'auto' });
    if (col.duration && colHasValue('duration')) enabledCols.push({ key: 'duration', label: pdfT('pdf.setlist.duration'), width: 'auto' });
    if (col.tempo && colHasValue('tempo')) enabledCols.push({ key: 'tempo', label: pdfT('pdf.setlist.tempo'), width: 'auto' });
    if (col.key && colHasValue('key')) enabledCols.push({ key: 'key', label: pdfT('pdf.setlist.key'), width: 'auto' });
    if (enabledCols.length === 0) enabledCols.push({ key: 'title', label: pdfT('pdf.setlist.title'), width: '*' });
    const activeCount = enabledCols.length;

    // Per-cell value accessor, matching the old fixed columns.
    const cellValue = (key: keyof SetlistPdfColumns, s: SetlistSongDTO, songNum: number): string => {
      let v: string;
      switch (key) {
        case 'showNumber': v = String(songNum); break;
        case 'title': v = s.title || '—'; break;
        case 'remarks': v = s.remarks || '—'; break;
        case 'duration': v = s.duration || '—'; break;
        case 'tempo': v = s.tempo || '—'; break;
        case 'key': v = s.keys || '—'; break;
        default: v = '';
      }
      return st.uppercase ? v.toUpperCase() : v;
    };

    body.push({ text: st.uppercase ? pdfT('pdf.setlist.setlist') : pdfT('pdf.setlist.setlistLower'), style: 'section' });
    body.push({
      table: {
        headerRows: col.showTableHeader ? 1 : 0,
        widths: enabledCols.map((c) => c.width),
        body: [
          ...(col.showTableHeader ? [enabledCols.map((c) => ({ text: st.uppercase ? c.label.toUpperCase() : c.label, style: 'th', ...cellPad }))] : []),
          ...songs.map((s, i) => {
            if (s.is_separator) {
              return [{ text: s.title || '—', style: 'sep', colSpan: activeCount, alignment: 'left', ...cellPad }];
            }
            const zebra = i % 2 === 1 ? { fillColor: st.zebraFill } : {};
            const songNum = songs.slice(0, i).filter((x) => !x.is_separator).length + 1;
            return enabledCols.map((c) => ({
              text: cellValue(c.key, s, songNum),
              style: 'td',
              ...zebra,
              ...cellPad,
            }));
          }),
        ],
        // `layout: 'noBorders'` is the reliable pdfmake way to drop ALL grid
        // lines (the per-line hLineWidth/vLineColor functions can be ignored in
        // this build). Zebra striping still comes from the per-cell fillColor
        // above. When the grid is on, use a custom layout with visible lines.
        layout: st.showGrid ? {
          hLineWidth: () => 0.5,
          vLineWidth: () => 0.5,
          hLineColor: () => '#dddddd',
          vLineColor: () => '#dddddd',
          fillColor: (rowIndex: number) => (rowIndex > 0 && rowIndex % 2 === 0 ? st.zebraFill : null),
        } : 'noBorders',
      },
    });
    if (totalSeconds > 0) {
      body.push({ text: pdfT('pdf.setlist.totalDuration', { duration: formatMmSs(totalSeconds) }), style: 'total', alignment: 'right' });
    }
  }

  if (setlist.notes) {
    body.push({ text: ' ', margin: [0, 12, 0, 4] });
    body.push({ text: pdfT('pdf.setlist.notes'), style: 'section' });
    body.push({ text: setlist.notes, style: 'notes' });
  }

  return {
    pageSize: 'A4',
    pageMargins: [40, 40, 40, 40],
    content: body,
    // Pagination for multi-page setlists: same "Page X / Y" info shown in the
    // header (under the setlist name) and the footer (bottom right).
    // Sent as SERIALIZABLE specs — a pdfmake closure would be dropped by
    // JSON.stringify; the server rebuilds the function and fills {page}/{pages}.
    ...(st.multiPage ? {
      headerSpec: {
        text: pdfT('pdf.setlist.pageXofY', { current: '{page}', total: '{pages}' }),
        alignment: 'right',
        fontSize: 9,
        color: '#888',
        margin: [0, 14, 40, 0],
      },
      footerSpec: {
        text: pdfT('pdf.setlist.pageXofY', { current: '{page}', total: '{pages}' }),
        alignment: 'right',
        fontSize: 9,
        color: '#888',
        margin: [0, 0, 40, 14],
      },
    } : {}),
    styles: {
      band: { fontSize: 22, bold: true, color: '#333' },
      title: { fontSize: 26, bold: true, color: '#222' },
      subtitle: { fontSize: 12, bold: true, color: '#555', margin: [0, 4, 0, 2] },
      date: { fontSize: 12, color: '#666', margin: [0, 2, 0, 2] },
      venue: { fontSize: 12, color: '#666', italics: true, margin: [0, 0, 0, 8] },
      section: { fontSize: 13, bold: true, margin: [0, 12, 0, 6] },
      th: { bold: true, fontSize: st.bodyFontSize, fillColor: st.headerFill },
      td: { fontSize: st.bodyFontSize },
      sep: { bold: true, fontSize: st.bodyFontSize, color: '#333', fillColor: '#ececec' },
      total: { fontSize: st.bodyFontSize, bold: true, margin: [8, 8, 0, 0] },
      notes: { fontSize: 10, color: '#555' },
    },
  };
}

